import type {ErrorInfo} from 'react';

import {captureReactException, reactErrorHandler} from '@sentry/react';

/**
 * React 19 root error hooks (CAD-6757). Kept in their own module so the
 * regression test drives the exact configuration `src/app.tsx` ships.
 *
 * Capture ordering is the reason these are hand-rolled instead of three
 * `reactErrorHandler` calls. When a boundary catches, React runs the root hook
 * BEFORE the boundary's `componentDidCatch` (react-dom's
 * `initializeClassErrorUpdate` invokes `logCaughtError` first), and
 * `captureException` drops the second capture of the same Error instance
 * (`__sentry_captured__`). Whichever runs first therefore owns the event and
 * its scope — so `onCaughtError` must not capture, or the boundary's
 * `error_boundary` / `feature` tags never reach Sentry.
 */
export const rootErrorHandlers = {
  /**
   * No boundary caught it — the app is gone. Captured explicitly rather than
   * through `reactErrorHandler(callback)`, which derives `mechanism.handled`
   * from whether a callback was passed and would file a fatal crash as handled.
   */
  onUncaughtError: (error: unknown, errorInfo: ErrorInfo): void => {
    captureReactException(error, errorInfo, {
      mechanism: {handled: false, type: 'auto.function.react.error_handler'},
    });
    console.warn('Uncaught React error', error, errorInfo.componentStack);
  },

  /**
   * Deliberately does not capture: the ErrorBoundary that caught this reports
   * it with its isolation-point tags. The hook exists only to replace React's
   * default `console.error` logging, which `captureConsole` (assert/error)
   * would otherwise turn into a duplicate message event. `console.warn` is not
   * captured and survives production builds (vite `esbuild.pure`).
   */
  onCaughtError: (error: unknown, errorInfo: ErrorInfo): void => {
    console.warn('React error boundary caught', error, errorInfo.componentStack);
  },

  /** React recovered on its own (concurrent retry, hydration mismatch): genuinely handled. */
  onRecoverableError: reactErrorHandler((error: unknown, errorInfo: ErrorInfo) => {
    console.warn('Recoverable React error', error, errorInfo.componentStack);
  }),
};
